import { API_HOST } from '@/constants/config';

const toQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

/**
 * Error carrying the HTTP status, so callers can tell "this tag does not exist"
 * from "the request failed". `/tags/:slug` is an indexed page, and treating a
 * 500 or a dropped connection as a missing tag would emit `noindex` on a page
 * that is perfectly fine.
 */
const httpError = (message, status) => Object.assign(new Error(message), { status });

const fetchTopicTags = async (path, params = {}) => {
  const url = `${API_HOST}${path}${toQueryString(params)}`;
  let response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Platform': 'web',
      },
    });
  } catch {
    throw httpError('Network error — unable to reach the topic tags service.', 0);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw httpError(
      response.ok
        ? 'Topic tags service returned an unexpected response.'
        : `Topic tags service returned ${response.status}.`,
      response.status,
    );
  }

  const data = await response.json();
  if (!response.ok || data?.error) {
    throw httpError(data?.message || 'Failed to fetch topic tags data', response.status);
  }
  return data;
};

export const topicTagApiSsr = {
  listSeoSlugs: async () => {
    const data = await fetchTopicTags('/json/topic-tags/seo-slugs');
    return data.slugs || [];
  },

  list: async ({
    page = 1,
    limit = 50,
    q,
    featured = false,
    withTopics = true,
  } = {}) => {
    const data = await fetchTopicTags('/json/topic-tags', {
      page,
      limit,
      q: q?.trim() || undefined,
      featured: featured ? 'true' : undefined,
      withTopics: withTopics ? 'true' : 'false',
    });
    return data || { topicTags: [], pagination: null };
  },

  getBySlug: async (slug) => {
    if (!slug) return null;
    const data = await fetchTopicTags(`/json/topic-tags/by-slug/${encodeURIComponent(slug)}`);
    return data.topicTag || null;
  },

  getTopicsBySlug: async (slug, { page = 1, limit = 24, q } = {}) => {
    if (!slug) return null;
    const data = await fetchTopicTags(
      `/json/topic-tags/by-slug/${encodeURIComponent(slug)}/topics`,
      { page, limit, q: q?.trim() || undefined },
    );
    return data || null;
  },
};
